import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import type { IAumMenuItem, ITableOptions, ModalData } from '@modeso/aumico-lib-core-fe';
import {
    AumicoCoreProvider,
    DeleteTextSnippetsDialogActions,
    EMPTY_ARRAY_LENGTH,
    ModalComponent,
    ModalTitleIcons,
    NotificationType,
} from '@modeso/aumico-lib-core-fe';
import type { ITextSnippetDTO } from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import { I18NextService } from 'angular-i18next';
import Debug from 'debug';
import { filter, first } from 'rxjs/operators';
import { OnDeleteTextSnippetsFailed, OnDeleteTextSnippetsSuccessfully } from '../../actions/text-snippet.action';
import { TextSnippetsListMenuActions } from '../../enums/text-snippet-list-menu-actions.enum';
import { TextSnippetProvider } from '../../provider/text-snippet.provider';
import { localizationKeys } from './../../constants/localizationKeys';
const debug = Debug('aumico:TextSnippetsListComponent');

const COMPONENT_LOCALIZATION_KEYS = [
    localizationKeys.aumico_reports_organization_settings_text_snippets_list_title_column_header,
    localizationKeys.aumico_reports_organization_settings_text_snippets_list_modified_by_column_header,
    localizationKeys.aumico_reports_organization_settings_text_snippets_list_last_modified_column_header,
    localizationKeys.aumico_reports_organization_settings_text_snippets_list_description_column_header,
    localizationKeys.aumico_reports_organization_settings_text_snippets_list_created_by_column_header,
    localizationKeys.aumico_reports_organization_settings_text_snippets_list_created_at_column_header,
    localizationKeys.aumico_reports_organization_settings_text_snippets_list_delete_button,
];

const SUCCESS_DESCRIPTION_LOCALIZATION_KEY =
    localizationKeys.aumico_reports_organization_settings_text_snippets_list_delete_success_notification_description;
const SUCCESS_TITLE_LOCALIZATION_KEY =
    localizationKeys.aumico_reports_organization_settings_text_snippets_list_delete_success_notification_title;
const ERROR_TITLE_LOCALIZATION_KEY =
    localizationKeys.aumico_reports_organization_settings_text_snippets_list_delete_error_notification_title;
const ERROR_DESCRIPTION_LOCALIZATION_KEY =
    localizationKeys.aumico_reports_organization_settings_text_snippets_list_delete_error_notification_description;
const DELETE_DIALOG_YES_BUTTON =
    localizationKeys.aumico_reports_organization_settings_text_snippets_list_delete_dialog_yes_button;
const DELETE_DIALOG_CANCEL_BUTTON =
    localizationKeys.aumico_reports_organization_settings_text_snippets_list_delete_dialog_cancel_button;
const DELETE_DIALOG_DESCRIPTION =
    localizationKeys.aumico_reports_organization_settings_text_snippets_list_delete_dialog_description;

@UntilDestroy()
@Component({
    selector: 'lib-text-snippets-list-component',
    templateUrl: 'text-snippets-list.component.html',
    styleUrls: ['text-snippets-list.component.scss'],
})
export class TextSnippetsListComponent implements OnInit, AfterViewInit {
    public data: ITextSnippetDTO[] = [];
    public options: ITableOptions | undefined;

    private selectedRows: ITextSnippetDTO[] = [];
    private _filterText: string = '';
    public filteredData: ITextSnippetDTO[];

    public menuItems: IAumMenuItem[] = [];

    @ViewChild('pageWrapper') pageWrapper!: ElementRef;
    private readonly DEFAULT_TABLE_HEIGHT = 200;

    get filterText(): string {
        return this._filterText;
    }

    set filterText(value: string) {
        this._filterText = value.toLowerCase();
        this.applyFilter();
        this.updateTableHeight();
    }

    @Output() search = new EventEmitter<string>();
    @Output() addNewTextSnippetClicked = new EventEmitter<void>();
    searchForm: FormGroup;

    constructor(
        private readonly textSnippetProvider: TextSnippetProvider,
        private readonly localizedTexts: I18NextService,
        private readonly dialog: MatDialog,
        private readonly coreProvider: AumicoCoreProvider,
        private readonly actionsListener$: ActionsSubject,
        private readonly fb: FormBuilder,
        private readonly cdr: ChangeDetectorRef,
    ) { }

    ngOnInit(): void {
        this.textSnippetProvider
            .getAllTextSnippets$()
            .pipe(untilDestroyed(this))
            .subscribe((textSnippetslist) => {
                this.data = textSnippetslist;
                this.applyFilter();
                setTimeout(() => {
                    this.updateTableHeight();
                    this.cdr.detectChanges();
                });
            });
        this.textSnippetProvider.dispatchTextSnippetsList();

        this.getMenuOptions();

        this.actionsListener$.pipe(ofType(OnDeleteTextSnippetsFailed), untilDestroyed(this)).subscribe(() => {
            this.fireErrorNotification();
        });

        this.actionsListener$.pipe(ofType(OnDeleteTextSnippetsSuccessfully), untilDestroyed(this)).subscribe(() => {
            this.fireSuccessNotification();
            this.selectedRows = [];
            this.textSnippetProvider.dispatchTextSnippetsList();
        });

        this.searchForm = this.fb.group({
            searchTerm: [''],
        });

        // Remove initial table height update from ngOnInit
    }

    ngAfterViewInit(): void {
        // Ensure pageWrapper is available before calculating height
        this.updateTableHeight();
        this.cdr.detectChanges();
    }
    @HostListener('window:resize', [])
    onWindowResize(): void {
        this.updateTableHeight();
    }

    private updateTableHeight(): void {
        if (this.pageWrapper && this.pageWrapper.nativeElement) {
            const pageWrapperElement = this.pageWrapper.nativeElement as HTMLElement;
            const rect = pageWrapperElement.getBoundingClientRect();
            const distanceFromTop = rect.top;
            const staticHeightOfTopNavBar = 35; // Adjust if needed
            const availableHeight = window.innerHeight - distanceFromTop - staticHeightOfTopNavBar;
            const tableMaxHeight = Math.max(this.DEFAULT_TABLE_HEIGHT, availableHeight);
            pageWrapperElement.style.setProperty('--ham-table-max-h', `${tableMaxHeight}px`);
        }
    }

    private applyFilter(): void {
        if (this._filterText) {
            this.filteredData = this.data.filter((item) => item.title.toLowerCase().includes(this._filterText));
        } else {
            this.filteredData = [...this.data];
        }
        setTimeout(() => {
            this.updateTableHeight();
            this.cdr.detectChanges();
        });
    }

    resetFilter(): void {
        this.filterText = '';
    }

    filterItems(): void {
        this.filterText = this.searchForm.get('searchTerm').value;
        this.applyFilter();
        this.search.emit();
    }

    private getMenuOptions(): void {
        this.menuItems = [
            {
                caption: this.localizedTexts?.t(
                    localizationKeys.aumico_reports_organization_settings_text_snippets_list_delete_button,
                ) as string,
                action: TextSnippetsListMenuActions.DELETE,
                icon: 'assets/hamIcons/trash-outline.svg',
            },
        ];
    }

    onMenuItemClicked(action: string): void {
        if (
            action === TextSnippetsListMenuActions.DELETE &&
            this.selectedRows != null &&
            this.selectedRows.length > EMPTY_ARRAY_LENGTH
        ) {
            this.handleDecisionDialog();
        }
    }

    public onCheckBoxSelectionChanged(selectedRows: ITextSnippetDTO[]): void {
        this.selectedRows = selectedRows;
    }

    public onRowButtonClicked(textSnippet: ITextSnippetDTO): void {
        this.textSnippetProvider.dispatchGetTextSnippetById(textSnippet._id);
    }

    private handleDecisionDialog(): void {
        const dialogRef = this.dialog.open<ModalComponent, ModalData, DeleteTextSnippetsDialogActions>(ModalComponent, {
            disableClose: false,
            panelClass: 'custom-modalbox',
            data: {
                shouldCloseDialog: false,
                showCloseButton: true,
                titleIcon: ModalTitleIcons.INFO,
                footerButtons: [
                    {
                        localizationKey: DELETE_DIALOG_YES_BUTTON,
                        action: DeleteTextSnippetsDialogActions.DELETE,
                        buttonColor: 'primary',
                    },
                    {
                        localizationKey: DELETE_DIALOG_CANCEL_BUTTON,
                        action: DeleteTextSnippetsDialogActions.CANCEL,
                        buttonColor: 'basic',
                    },
                ],
                content: {
                    text: [DELETE_DIALOG_DESCRIPTION],
                    icon: 'document-information',
                },
            },
        });

        dialogRef
            ?.afterClosed()
            .pipe(
                first(),
                filter((resultAction) => resultAction != null),
                untilDestroyed(this),
            )
            .subscribe((resultAction) => {
                if (resultAction === DeleteTextSnippetsDialogActions.DELETE) {
                    this.deleteTextSnippets();
                }
            });
    }

    private deleteTextSnippets(): void {
        const selectedTextSnippetsIds = this.getSelectedTextSnippetsIds();
        this.textSnippetProvider.dispatchDeleteTextSnippets(selectedTextSnippetsIds);
    }

    private getSelectedTextSnippetsIds(): string[] {
        const selectedTextSnippetsIds: string[] = [];
        this.selectedRows.forEach((selectedTextSnippet: ITextSnippetDTO) => {
            if (selectedTextSnippet._id == null) {
                throw new Error('There is a text snippet with id undefined');
            }
            selectedTextSnippetsIds.push(selectedTextSnippet._id);
        });
        return selectedTextSnippetsIds;
    }

    private fireSuccessNotification(): void {
        this.coreProvider.dispatchAddNotification(
            SUCCESS_TITLE_LOCALIZATION_KEY,
            SUCCESS_DESCRIPTION_LOCALIZATION_KEY,
            NotificationType.SUCCESS,
        );
    }

    private fireErrorNotification(): void {
        this.coreProvider.dispatchAddNotification(
            ERROR_TITLE_LOCALIZATION_KEY,
            ERROR_DESCRIPTION_LOCALIZATION_KEY,
            NotificationType.ERROR,
        );
    }

    public addNewTextSnippet(): void {
        this.addNewTextSnippetClicked.emit();
    }
}
